import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

type Variant = "success" | "error" | "warning" | "info";

export interface ToastOptions {
  duration?: number;
}

interface ToastItem {
  id: number;
  variant: Variant;
  title: string;
  message?: string;
}

interface ToastContextValue {
  toast: (variant: Variant, title: string, message?: string, options?: ToastOptions) => void;
  success: (title: string, message?: string, options?: ToastOptions) => void;
  error: (title: string, message?: string, options?: ToastOptions) => void;
  warning: (title: string, message?: string, options?: ToastOptions) => void;
  info: (title: string, message?: string, options?: ToastOptions) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const VARIANT_STYLE: Record<Variant, { icon: string; ring: string; bar: string }> = {
  success: { icon: "ri-checkbox-circle-fill text-success-500", ring: "text-success-500", bar: "bg-success-500" },
  error: { icon: "ri-close-circle-fill text-danger-500", ring: "text-danger-500", bar: "bg-danger-500" },
  warning: { icon: "ri-error-warning-fill text-warning-500", ring: "text-warning-500", bar: "bg-warning-500" },
  info: { icon: "ri-information-fill text-info-500", ring: "text-info-500", bar: "bg-info-500" },
};

let toastCounter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (variant: Variant, title: string, message?: string, options?: ToastOptions) => {
      const id = ++toastCounter;
      const item: ToastItem = { id, variant, title, message };
      setItems((prev) => [...prev.slice(-4), item]);
      const duration = options?.duration ?? 4000;
      window.setTimeout(() => dismiss(id), duration);
    },
    [dismiss]
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      toast,
      success: (t, m, o) => toast("success", t, m, o),
      error: (t, m, o) => toast("error", t, m, o),
      warning: (t, m, o) => toast("warning", t, m, o),
      info: (t, m, o) => toast("info", t, m, o),
      dismiss,
    }),
    [toast, dismiss]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="no-print fixed right-4 top-4 z-[80] flex w-80 flex-col gap-2" role="region" aria-label="Notifications">
        {items.map((item) => {
          const style = VARIANT_STYLE[item.variant];
          return (
            <div
              key={item.id}
              className="pointer-events-auto flex items-start gap-3 overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-lg shadow-foreground-950/5"
            >
              <div className={`h-full w-1 self-stretch ${style.bar}`} />
              <div className={`mt-3 flex h-6 w-6 flex-none items-center justify-center ${style.ring}`}>
                <i className={`${style.icon} text-xl`} />
              </div>
              <div className="flex-1 py-3 pr-3">
                <p className="text-sm font-semibold text-foreground-900">{item.title}</p>
                {item.message ? <p className="mt-0.5 text-xs text-foreground-600">{item.message}</p> : null}
              </div>
              <button
                type="button"
                onClick={() => dismiss(item.id)}
                aria-label="Dismiss"
                className="mt-2 mr-2 flex h-6 w-6 flex-none cursor-pointer items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 hover:text-foreground-700"
              >
                <i className="ri-close-line text-base" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}